
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { DesignerOptions } from '@r-core';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { getDimensionMeasurement } from '@r-core/helpers';
import { PanelItem } from '@rta/rta-blueprint-library';
import { useSvgBox } from '@r-hooks';

export type DrawEarDimensionParams = {
	panelItem: PanelItem;
	options: DesignerOptions;
	earOverlap?: boolean;
}

export const DrawEarDimension = ({ panelItem, options, earOverlap = false }: DrawEarDimensionParams): React.ReactElement => {
	const { box: boxVerticalText, refText: refVerticalText } = useSvgBox();
	const { box: boxHorizontalText, refText: refHorizontalText } = useSvgBox();
	const { box: boxVerticalTextMirrored, refText: refVerticalTextMirrored } = useSvgBox();
	const { box: boxHorizontalTextMirrored, refText: refHorizontalTextMirrored } = useSvgBox();

	const fontSize = DIMENSION_FONT_SIZE;
	const segmentLength = 4;
	const segmentAngle = -60 * (Math.PI / 180);
	const verticalTextMargin = 0.325;
	const strokeWidth = 0.125;
	const { island, panel } = panelItem.refMap;

	const ear = {
		height: island.frontPanel.countertopOverhang,
		width: panelItem.earSize
	};

	const vDimension = getDimensionMeasurement(ear.height, options.useFractions);
	const hDimension = getDimensionMeasurement(ear.width, options.useFractions);

	// Original (right side) points
	const horizontal = {
		p1: {
			x: (panelItem.size.width / 2) - ear.width,
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		},
		p2: {
			x: (panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		}
	};

	const vertical = {
		p1: {
			x: (panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		},
		p2: {
			x: (panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2) + ear.height
		}
	};

	// Mirrored (left side) points
	const horizontalMirrored = {
		p1: {
			x: -(panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		},
		p2: {
			x: -(panelItem.size.width / 2) + ear.width,
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		}
	};

	const verticalMirrored = {
		p1: {
			x: -(panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2)
		},
		p2: {
			x: -(panelItem.size.width / 2),
			y: ((panelItem.size.depth - panelItem.item.distanceFrontOfFace) / 2) + ear.height
		}
	};

	// Original segments
	const vSegments = {
		s1: {
			start: vertical.p1,
			end: {
				x: vertical.p1.x + boxVerticalText.width + verticalTextMargin,
				y: vertical.p1.y
			}
		},
		s2: {
			start: vertical.p2,
			end: {
				x: vertical.p2.x + boxVerticalText.width + verticalTextMargin,
				y: vertical.p2.y
			}
		}
	};

	const hSegments = {
		s1: {
			start: horizontal.p1,
			end: {
				x: horizontal.p1.x + (Math.cos(segmentAngle) * (segmentLength + (ear.width / Math.cos(segmentAngle)))),
				y: horizontal.p1.y + (Math.sin(segmentAngle) * segmentLength) - boxHorizontalText.height // that
			}
		},
		s2: {
			start: horizontal.p2,
			end: {
				x: horizontal.p2.x + (Math.cos(segmentAngle) * segmentLength),
				y: horizontal.p2.y + (Math.sin(segmentAngle) * segmentLength) // this - that
			}
		}
	};

	const mirroredOffsetY = (hSegments.s2.end.y - hSegments.s1.end.y) + (strokeWidth * 2);

	// Mirrored segments
	const vSegmentsMirrored = {
		s1: {
			start: verticalMirrored.p1,
			end: {
				x: verticalMirrored.p1.x - boxVerticalTextMirrored.width - verticalTextMargin,
				y: verticalMirrored.p1.y
			}
		},
		s2: {
			start: verticalMirrored.p2,
			end: {
				x: verticalMirrored.p2.x - boxVerticalTextMirrored.width - verticalTextMargin,
				y: verticalMirrored.p2.y
			}
		}
	};

	const hSegmentsMirrored = {
		s1: {
			start: horizontalMirrored.p1,
			end: {
				x: horizontalMirrored.p1.x - (Math.cos(segmentAngle) * segmentLength),
				y: (horizontalMirrored.p1.y + (Math.sin(segmentAngle) * segmentLength)) + mirroredOffsetY
			}
		},
		s2: {
			start: horizontalMirrored.p2,
			end: {
				x: horizontalMirrored.p2.x - (Math.cos(segmentAngle) * (segmentLength + (ear.width / Math.cos(segmentAngle)))),
				y: (horizontalMirrored.p2.y + (Math.sin(segmentAngle) * segmentLength) - boxHorizontalTextMirrored.height) + mirroredOffsetY
			}
		}
	};


	if (panelItem.sleeve !== null) return <></>;

	return (
		!earOverlap
			? <>
				{/* Original (right side) dimensions */}
				<line x1={vSegments.s1.start.x} x2={vSegments.s1.end.x} y1={vSegments.s1.start.y} y2={vSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={vSegments.s2.start.x} x2={vSegments.s2.end.x} y1={vSegments.s2.start.y} y2={vSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refVerticalText} textAnchor="start" fontSize={fontSize}
					x={vSegments.s1.start.x + verticalTextMargin} y={vSegments.s2.end.y - ((ear.height - boxVerticalText.height) / 2) - 0.3125}
					style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{vDimension}&quot;
				</text>

				<line x1={hSegments.s1.start.x} x2={hSegments.s1.end.x} y1={hSegments.s1.start.y} y2={hSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s1.end.x} x2={hSegments.s1.end.x + boxHorizontalText.width} y1={hSegments.s1.end.y} y2={hSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s2.start.x} x2={hSegments.s2.end.x} y1={hSegments.s2.start.y} y2={hSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s2.end.x} x2={hSegments.s2.end.x + boxHorizontalText.width} y1={hSegments.s2.end.y} y2={hSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refHorizontalText} textAnchor="start" fontSize={fontSize} x={hSegments.s2.end.x} y={hSegments.s2.end.y - 0.3125} style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{hDimension}&quot;</text>

				{/* Mirrored (left side) dimensions */}
				<line x1={vSegmentsMirrored.s1.start.x} x2={vSegmentsMirrored.s1.end.x} y1={vSegmentsMirrored.s1.start.y} y2={vSegmentsMirrored.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={vSegmentsMirrored.s2.start.x} x2={vSegmentsMirrored.s2.end.x} y1={vSegmentsMirrored.s2.start.y} y2={vSegmentsMirrored.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refVerticalTextMirrored} textAnchor="end" fontSize={fontSize}
					x={vSegmentsMirrored.s1.start.x - verticalTextMargin} y={vSegmentsMirrored.s2.end.y - ((ear.height - boxVerticalTextMirrored.height) / 2) - 0.3125}
					style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{vDimension}&quot;
				</text>

				<line x1={hSegmentsMirrored.s1.start.x} x2={hSegmentsMirrored.s1.end.x} y1={hSegmentsMirrored.s1.start.y} y2={hSegmentsMirrored.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegmentsMirrored.s1.end.x} x2={hSegmentsMirrored.s1.end.x - boxHorizontalTextMirrored.width} y1={hSegmentsMirrored.s1.end.y} y2={hSegmentsMirrored.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegmentsMirrored.s2.start.x} x2={hSegmentsMirrored.s2.end.x} y1={hSegmentsMirrored.s2.start.y} y2={hSegmentsMirrored.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegmentsMirrored.s2.end.x} x2={hSegmentsMirrored.s2.end.x - boxHorizontalTextMirrored.width} y1={hSegmentsMirrored.s2.end.y} y2={hSegmentsMirrored.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refHorizontalTextMirrored} textAnchor="end" fontSize={fontSize} x={hSegmentsMirrored.s2.end.x} y={(hSegmentsMirrored.s1.end.y - 0.3125)} style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{hDimension}&quot;</text>

				{ options.showDeveloperInsights && <>
					<circle cx={vertical.p1.x} cy={vertical.p1.y} r=".25" fill="red" />
					<circle cx={horizontal.p1.x} cy={horizontal.p1.y} r=".25" fill="green" />
					<circle cx={verticalMirrored.p1.x} cy={verticalMirrored.p1.y} r=".25" fill="red" />
					<circle cx={horizontalMirrored.p1.x} cy={horizontalMirrored.p1.y} r=".25" fill="green" />
				</>}
			</>
			: <>
				{/* Original (right side) dimensions */}
				<line x1={vSegments.s1.start.x} x2={vSegments.s1.end.x} y1={vSegments.s1.start.y} y2={vSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={vSegments.s2.start.x} x2={vSegments.s2.end.x} y1={vSegments.s2.start.y} y2={vSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refVerticalText} textAnchor="start" fontSize={fontSize}
					x={vSegments.s1.start.x + verticalTextMargin} y={vSegments.s2.end.y - ((ear.height - boxVerticalText.height) / 2) - 0.3125}
					style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{vDimension}&quot;
				</text>

				<line x1={hSegments.s1.start.x} x2={hSegments.s1.end.x} y1={hSegments.s1.start.y} y2={hSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s1.end.x} x2={hSegments.s1.end.x + boxHorizontalText.width} y1={hSegments.s1.end.y} y2={hSegments.s1.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s2.start.x} x2={hSegments.s2.end.x} y1={hSegments.s2.start.y} y2={hSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<line x1={hSegments.s2.end.x} x2={hSegments.s2.end.x + boxHorizontalText.width} y1={hSegments.s2.end.y} y2={hSegments.s2.end.y} stroke="red" strokeWidth={strokeWidth}/>
				<text ref={refHorizontalText} textAnchor="start" fontSize={fontSize} x={hSegments.s2.end.x} y={hSegments.s2.end.y - 0.3125} style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{hDimension}&quot;</text>

				{/* Mirrored (left side) dimensions */}
				<text ref={refVerticalTextMirrored} textAnchor="end" fontSize={fontSize}
					x={(-panelItem.item.size.width / 2) + 8} y={(vSegments.s1.end.y + ear.height + fontSize)}
					style={{ fill: 'red' }} className="rta-designer-component-measure rta-noselect">{hDimension}" X {vDimension}"
				</text>

				{ options.showDeveloperInsights && <>
					<circle cx={vertical.p1.x} cy={vertical.p1.y} r=".25" fill="red" />
					<circle cx={horizontal.p1.x} cy={horizontal.p1.y} r=".25" fill="green" />
					<circle cx={verticalMirrored.p1.x} cy={verticalMirrored.p1.y} r=".25" fill="red" />
					<circle cx={horizontalMirrored.p1.x} cy={horizontalMirrored.p1.y} r=".25" fill="green" />
				</>}
			</>
	);
};
